import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@/utils/auth/auth0'
import * as API from '../../api/regionsAPI'

export function useRegions(token: string) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['regions'],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchRegions(freshToken)
    },
    enabled: !!token,
    retry: 3,
  })

  return {
    regions: Array.isArray(query.data) ? query.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.error?.message,
  }
}

export function useRegionSets(token: string) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['regionSets'],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchRegionSets(freshToken)
    },
    enabled: !!token,
    retry: 3,
  })

  return {
    regionSets: Array.isArray(query.data) ? query.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.error?.message,
  }
}

interface UseRegionOpportunityCountParams {
  token: string
  regionIds: number[]
  acceptsGamblingProceeds: boolean
  acceptsInternational: boolean
  iwiIds?: number[]
}

export function useRegionOpportunityCount({
  token,
  regionIds,
  acceptsGamblingProceeds,
  acceptsInternational,
  iwiIds = [],
}: UseRegionOpportunityCountParams) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: [
      'regionOpportunityCount',
      regionIds,
      acceptsGamblingProceeds,
      acceptsInternational,
      iwiIds,
    ],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchRegionOpportunityCount(freshToken, {
        region_ids: regionIds,
        accepts_gambling_proceeds: acceptsGamblingProceeds,
        accepts_international: acceptsInternational,
        iwi_ids: iwiIds,
      })
    },
    enabled: !!token && regionIds.length > 0,
    retry: 1,
  })

  return {
    count: typeof query.data === 'number' ? query.data : undefined,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    errorMessage: query.error?.message,
  }
}
